import { useCallback } from "react";
import {
  PostmarkAttachmentDownloadDto,
  PostmarkBulkActionRequestDto,
  PostmarkConversationDetailResponseDto,
  PostmarkCreateDsrRequestDto,
  PostmarkCreateDsrResponseDto,
  PostmarkOrganizationConversationSummaryDto,
  PostmarkOrganizationMessageDto,
  PostmarkReplyRequestDto,
} from "../../common/service/server/dto/PostmarkInboxDto";
import { OrganizationPublicId, UUIDString } from "../../common/service/server/Types";
import { Api } from "../../common/service/Api";
import { useActionRequest, useDataRequest } from "../../common/hooks/api";
import { PaginatedDto } from "../../common/service/server/dto/PaginatedDto";

const ERROR_MESSAGES = {
  loadConversations: "Unable to load privacy emails.",
  loadConversation: "Unable to load conversation.",
  reply: "Failed to send reply.",
  archive: "Failed to update archive state.",
  markRead: "Failed to update read state.",
  bulk: "Failed to apply bulk action.",
  createDsr: "Failed to create request from email.",
  attachment: "Failed to download attachment.",
};

export const usePrivacyEmailConversations = (
  organizationId: OrganizationPublicId,
  params: { page?: number; per?: number; q?: string; status?: string } = {},
  enabled = true,
) => {
  const api = useCallback(
    () => Api.postmarkInbox.getConversations(organizationId, params),
    [organizationId, params],
  );

  return useDataRequest<PaginatedDto<PostmarkOrganizationConversationSummaryDto>>({
    queryKey: enabled ? ["postmarkInbox-conversations", organizationId, params] : [],
    api,
    messages: { defaultError: ERROR_MESSAGES.loadConversations },
    notReady: !enabled,
  });
};

export const usePrivacyEmailConversationDetail = (
  organizationId: OrganizationPublicId,
  conversationId: UUIDString,
  params: { page?: number; per?: number } = {},
  enabled = true,
) => {
  const api = useCallback(
    () => Api.postmarkInbox.getConversationDetail(organizationId, conversationId, params),
    [organizationId, conversationId, params],
  );

  return useDataRequest<PostmarkConversationDetailResponseDto>({
    queryKey: enabled ? ["postmarkInbox-conversation", organizationId, conversationId, params] : [],
    api,
    messages: { defaultError: ERROR_MESSAGES.loadConversation },
    notReady: !enabled,
  });
};

export const usePrivacyEmailReply = (
  organizationId: OrganizationPublicId,
  conversationId: UUIDString,
) =>
  useActionRequest<PostmarkOrganizationMessageDto, PostmarkReplyRequestDto>({
    api: (payload) => Api.postmarkInbox.reply(organizationId, conversationId, payload ?? {}),
    messages: { defaultError: ERROR_MESSAGES.reply, success: "Reply sent." },
    notifySuccess: true,
  });

export const usePrivacyEmailArchive = (
  organizationId: OrganizationPublicId,
  conversationId: UUIDString,
  action: "archive" | "unarchive",
) =>
  useActionRequest<PostmarkOrganizationConversationSummaryDto>({
    api: () =>
      action === "archive"
        ? Api.postmarkInbox.archive(organizationId, conversationId)
        : Api.postmarkInbox.unarchive(organizationId, conversationId),
    messages: { defaultError: ERROR_MESSAGES.archive },
  });

export const usePrivacyEmailMarkRead = (
  organizationId: OrganizationPublicId,
  conversationId: UUIDString,
  markAs: "read" | "unread",
) =>
  useActionRequest<PostmarkOrganizationConversationSummaryDto>({
    api: () =>
      markAs === "read"
        ? Api.postmarkInbox.markRead(organizationId, conversationId)
        : Api.postmarkInbox.markUnread(organizationId, conversationId),
    messages: { defaultError: ERROR_MESSAGES.markRead },
  });

export const usePrivacyEmailBulkAction = (organizationId: OrganizationPublicId) =>
  useActionRequest<PostmarkOrganizationConversationSummaryDto[], PostmarkBulkActionRequestDto>({
    api: (payload) =>
      Api.postmarkInbox.bulk(organizationId, payload ?? { ids: [], action: "ARCHIVE" }),
    messages: { defaultError: ERROR_MESSAGES.bulk, success: "Updated conversations." },
    notifySuccess: true,
  });

export const usePrivacyEmailCreateDsr = (
  organizationId: OrganizationPublicId,
  conversationId: UUIDString,
) =>
  useActionRequest<PostmarkCreateDsrResponseDto, PostmarkCreateDsrRequestDto>({
    api: (payload) => Api.postmarkInbox.createDsr(organizationId, conversationId, payload ?? {}),
    messages: { defaultError: ERROR_MESSAGES.createDsr, success: "Request created." },
    notifySuccess: true,
  });

export const usePrivacyEmailAttachmentDownload = (
  organizationId: OrganizationPublicId,
  messageId: UUIDString,
  attachmentId: UUIDString,
) =>
  useActionRequest<PostmarkAttachmentDownloadDto>({
    api: () => Api.postmarkInbox.downloadAttachment(organizationId, messageId, attachmentId),
    messages: { defaultError: ERROR_MESSAGES.attachment },
  });
